import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Button, VStack } from '@chakra-ui/react';
import { DropzoneField, FloatingInputField } from '@payler/ui-components';
import React, { useEffect, useState } from 'react';
import {
  useBulkRefundEncryptionKeyQuery,
  useBulkRefundDetailsQuery,
  useMakeBulkRefundMutation,
} from '../../hooks/payouts/queries';
import { encryptDataAESWithCBC } from '../../helpers/crypto';
import createLogger from 'debug';
import { byteArrayToBase64 } from '../../helpers/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  BulkRefundConfirmationModal,
  useCloseBulkRefundConfirmationModal,
  useShowBulkRefundConfirmationModal,
} from '../../modals/BulkRefundConfirmationModal';
import { useBulkPayoutErrorContext } from '../../context/BulkPayoutErrorContext';
import { useSelectedContract } from '../../hooks/contracts';

const log = createLogger('BulkRefundForm');

type TBulkRefundFormFields = {
  files: [File];
  password: string;
};

const useResolver = () => {
  const { t } = useTranslation(['returns']);
  return yupResolver(
    yup.object().shape({
      files: yup.array().required().length(1),
      password: yup.string().required(t('returns:form.invalidPassword')),
    }),
  );
};

export const BulkRefundForm = () => {
  const { t } = useTranslation(['returns']);
  const { isBulkRefundOverPayoutAllowed } = useSelectedContract() || {};
  const [isStartEncrypting, setIsStartEncrypting] = useState(false);
  const [bulkRefundId, setBulkRefundId] = useState<string | undefined>();
  const { clearBulkPayoutError, setBulkPayoutError } =
    useBulkPayoutErrorContext();
  const showBulkRefundConfirmationModal = useShowBulkRefundConfirmationModal();
  const closeBulkRefundConfirmationModal =
    useCloseBulkRefundConfirmationModal();
  const resolver = useResolver();
  const methods = useForm<TBulkRefundFormFields>({
    resolver,
    mode: 'onChange',
  });
  const {
    data: encryptKey,
    isSuccess: isLoadedEncryptKey,
    isLoading: isLoadingEncryptKey,
    isError: isRequestEncryptionKeyError,
    error: requestEncryptionKeyError,
  } = useBulkRefundEncryptionKeyQuery();
  const { data: bulkRefundFileInfoData, clearBulkRefundFileInfo } =
    useBulkRefundDetailsQuery(bulkRefundId);

  const { mutate, status } = useMakeBulkRefundMutation();
  const isButtonLoading = status === 'pending' || isStartEncrypting;
  const isButtonDisabled =
    isLoadingEncryptKey ||
    !isLoadedEncryptKey ||
    !isBulkRefundOverPayoutAllowed;

  useEffect(() => {
    if (isRequestEncryptionKeyError) {
      setBulkPayoutError(requestEncryptionKeyError);
    }
  }, [isRequestEncryptionKeyError, requestEncryptionKeyError, setBulkPayoutError]);

  useEffect(() => {
    if (bulkRefundFileInfoData && bulkRefundId) {
      const password = methods.getValues('password');
      const fileName = methods.getValues('files')[0].name;

      showBulkRefundConfirmationModal({
        ...bulkRefundFileInfoData,
        bulkRefundId,
        password,
        fileName,
      });
    }
  }, [bulkRefundFileInfoData, bulkRefundId, methods, showBulkRefundConfirmationModal]);

  const handleCloseConfirmationModal = () => {
    setBulkRefundId(undefined);
    clearBulkRefundFileInfo().then(closeBulkRefundConfirmationModal);
  };

  const handleConfirmBulkRefund = () => {
    methods.reset();
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    clearBulkPayoutError();

    try {
      const f = data?.files?.[0];
      if (!f) throw new Error('File not found in form data');
      if (!encryptKey) throw new Error('Encryption key is not loaded');

      const fileBuf = await f.arrayBuffer();

      setIsStartEncrypting(true);

      const { encryptedData, encryptedIV, encryptedKey } =
        await encryptDataAESWithCBC(fileBuf, encryptKey);

      const blob = new Blob([encryptedData], {
        type: 'application/octet-stream',
      });
      const encryptedFile = new File([blob], f.name, {
        type: 'application/octet-stream',
      });

      setIsStartEncrypting(false);

      mutate(
        {
          aesIv: byteArrayToBase64(encryptedIV),
          fileAesKey: byteArrayToBase64(encryptedKey),
          file: encryptedFile,
        },
        {
          onSuccess: ({ id }) => {
            setBulkRefundId(id);
            log('Bulk refund ID: %s', id);
          },
          onError: (err) => {
            setBulkPayoutError(err);
            log('Bulk refund mutation error: %s', err?.toString());
          },
        },
      );
    } catch (e) {
      setBulkPayoutError(e as Error);
      setIsStartEncrypting(false);
      log('Encryption error: %s', (e as Error)?.toString());
      console.error(e);
    }
  });

  return (
    <FormProvider {...methods}>
      <VStack as="form" w="full" onSubmit={handleSubmit}>
        <Box w="full">
          <DropzoneField
            accept={{ 'text/*': ['.csv'] }}
            title={t('returns:form.selectCsvFile')}
            description={t('returns:form.dragAndDropFileOrClickOnField')}
            help=""
            name="files"
            maxFiles={1}
          />
        </Box>
        <FloatingInputField
          name="password"
          label={t('returns:form.password')}
          type="password"
          autoComplete="new-password"
        />
        <Button
          type="submit"
          w="full"
          isDisabled={isButtonDisabled}
          isLoading={isButtonLoading}
        >
          {t('returns:form.send')}
        </Button>
        <BulkRefundConfirmationModal
          onClose={handleCloseConfirmationModal}
          onConfirm={handleConfirmBulkRefund}
        />
      </VStack>
    </FormProvider>
  );
};

